import { type AnalyticsInitConfig, AnalyticsService } from '@alison/analytics/client';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { router } from '../router.js';
import { useSession } from './authClient.js';
import { getSelectionCookie } from './cookies.js';
import { trpcClient } from './trpcClient.js';
import { useMe } from './useMe.js';

declare global {
  interface Window {
    // Injected at runtime by nginx from the MIXPANEL_TOKEN container var (see nginx.conf).
    // A non-empty token enables analytics; unset/empty (or the un-substituted placeholder) => off.
    __MIXPANEL_TOKEN?: string;
  }
}

// biome-ignore lint/suspicious/noTemplateCurlyInString: literal nginx un-substituted placeholder, not a template string
const MIXPANEL_TOKEN_PLACEHOLDER = '${MIXPANEL_TOKEN}';

const getAnalyticsConfig = (): AnalyticsInitConfig => {
  const injectedToken = window.__MIXPANEL_TOKEN;
  if (injectedToken && injectedToken !== MIXPANEL_TOKEN_PLACEHOLDER) {
    return { token: injectedToken, trackInternalUsers: false };
  }
  // Local dev has no nginx injection; allow a token from .env.local for testing.
  // Stripped from production builds — import.meta.env.DEV is statically false there.
  if (import.meta.env.DEV && import.meta.env.VITE_MIXPANEL_TOKEN) {
    return { token: import.meta.env.VITE_MIXPANEL_TOKEN, trackInternalUsers: true };
  }
  return { token: undefined, trackInternalUsers: false };
};

export function AnalyticsBootstrap(): null {
  const { data: session } = useSession();
  const { pathname } = useLocation();
  // me.get needs the brand-scoped app JWT, which does not exist on /login routes
  // (between SSO and brand selection) nor on /invites (the invitee has no account
  // yet). Querying it there 401s into the session-expired redirect, which
  // full-reloads and loops. Only query in the app.
  const isAuthenticatedRoute = !pathname.startsWith('/login') && !pathname.startsWith('/invites');
  const meQuery = useMe({
    enabled: session !== null && session !== undefined && isAuthenticatedRoute,
  });
  const identifiedUserIdRef = useRef<number | null>(null);
  const lastPageViewRef = useRef<string | null>(null);
  const [identified, setIdentified] = useState(false);

  useEffect(() => {
    AnalyticsService.init(getAnalyticsConfig());
  }, []);

  // Page views wait for identify: exclusion of internal users is only known once the
  // user is identified, and page views fire before that (mount, /login) would land in
  // Mixpanel for them anyway.
  useEffect(() => {
    if (!identified) return;
    // onResolved fires on every route resolution, including search-param changes
    // (the filter bars rewrite them constantly) and the initial navigation already
    // covered by the mount call. Only emit when the resolved page actually changes.
    const trackCurrentRoute = (): void => {
      const { location, matches } = router.state;
      const routeId = matches.at(-1)?.routeId ?? location.pathname;
      const pageKey = `${location.pathname}|${routeId}`;
      if (lastPageViewRef.current === pageKey) return;
      lastPageViewRef.current = pageKey;
      AnalyticsService.trackPageView(location.pathname, routeId);
    };
    trackCurrentRoute();
    return router.subscribe('onResolved', trackCurrentRoute);
  }, [identified]);

  useEffect(() => {
    if (session === null || session === undefined) {
      if (identifiedUserIdRef.current !== null) {
        AnalyticsService.logout();
        identifiedUserIdRef.current = null;
        lastPageViewRef.current = null;
        setIdentified(false);
      }
      return;
    }
    const me = meQuery.data;
    if (me === undefined || identifiedUserIdRef.current === me.userId) {
      return;
    }
    AnalyticsService.identify({
      userId: me.userId,
      email: session.user.email,
      name: session.user.name ?? null,
      organizationId: me.organizationId,
      organizationName: me.organizationName,
    });
    AnalyticsService.register({ accountIds: getSelectionCookie() });
    AnalyticsService.setRelay((events, { keepalive }) =>
      trpcClient.analytics.track.mutate({ events }, { context: { keepalive, telemetry: true } }),
    );
    void trpcClient.analytics.identify.mutate({ name: session.user.name ?? null });
    identifiedUserIdRef.current = me.userId;
    setIdentified(true);
  }, [session, meQuery.data]);

  return null;
}
